/**
 * Staff-side: manage customer portal login accounts. Each account is tied
 * to a CRM Company and matches its own party_name (+ aliases) against
 * Challans to show that customer their order history — see
 * routers/customer_portal.py's docstring for the matching design.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, X, Loader2, Copy, KeyRound } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { apiGet, apiPost, apiPatch } from '../../lib/api';

function Field({ label, children }) {
  return <div><label className="label">{label}</label>{children}</div>;
}

function ModalShell({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-[1000] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-sm w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <div className="font-display font-bold text-sm">{title}</div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700"><X size={16} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

export default function PortalAccounts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [credModal, setCredModal] = useState(null); // {email, temp_password}

  const load = () => {
    setLoading(true);
    apiGet('/portal/admin/accounts').then((r) => setItems(r.items || []))
      .catch(() => toast.error('Failed to load portal accounts')).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const toggleActive = async (a) => {
    try {
      await apiPatch(`/portal/admin/accounts/${a.id}`, { is_active: !a.is_active });
      toast.success(`${a.email} ${a.is_active ? 'deactivated' : 'activated'}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to update'); }
  };

  const resetPassword = async (a) => {
    try {
      const res = await apiPatch(`/portal/admin/accounts/${a.id}`, { reset_password: true });
      setCredModal({ email: a.email, temp_password: res.temp_password });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to reset password'); }
  };

  return (
    <div data-testid="portal-accounts-page">
      <PageHeader overline="Business Development" title="Customer Portal Accounts"
        subtitle="Give buyers a login to see their orders and request new supply." />
      <div className="flex items-center justify-between mb-4">
        <div className="kpi-overline">Accounts · {items.length}</div>
        <button onClick={() => setShowAdd(true)}
          className="px-4 py-2 bg-[#1F7A6E] hover:bg-[#14504E] text-white text-xs font-display font-bold uppercase tracking-widest rounded-sm flex items-center gap-2"
          data-testid="portal-account-add-btn">
          <Plus size={13} /> New Account
        </button>
      </div>
      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full dense-table">
          <thead><tr>
            <th>Company</th><th>Contact</th><th>Email</th><th>Phone</th><th>Status</th><th></th>
          </tr></thead>
          <tbody>
            {loading && <tr><td colSpan="6" className="text-center py-8 text-slate-400">Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan="6" className="text-center py-8 text-slate-400">No portal accounts yet.</td></tr>}
            {items.map((a) => (
              <tr key={a.id} data-testid={`portal-account-row-${a.id}`}>
                <td className="font-display font-bold">{a.company_name}</td>
                <td>{a.contact_name}</td>
                <td className="font-mono text-xs">{a.email}</td>
                <td className="text-xs">{a.phone || '—'}</td>
                <td><span className={`tag ${a.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{a.is_active ? 'active' : 'inactive'}</span></td>
                <td className="text-right whitespace-nowrap">
                  <button onClick={() => resetPassword(a)} className="text-xs text-[#1F7A6E] hover:underline mr-3 inline-flex items-center gap-1"><KeyRound size={11} /> Reset</button>
                  <button onClick={() => toggleActive(a)} className="text-xs text-slate-500 hover:underline">{a.is_active ? 'Deactivate' : 'Activate'}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showAdd && <AddAccountModal onClose={() => setShowAdd(false)} onCreated={(cred) => { setShowAdd(false); load(); setCredModal(cred); }} />}
      {credModal && <CredentialModal cred={credModal} onClose={() => setCredModal(null)} />}
    </div>
  );
}

function AddAccountModal({ onClose, onCreated }) {
  const [companies, setCompanies] = useState([]);
  const [form, setForm] = useState({ company_id: '', contact_name: '', email: '', phone: '' });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => { apiGet('/crm/companies?scope=all').then((r) => setCompanies(r.companies || r.items || [])).catch(() => {}); }, []);

  const save = async () => {
    if (!form.company_id) return toast.error('Select a company');
    if (!form.contact_name.trim()) return toast.error('Contact name is required');
    if (!form.email.trim()) return toast.error('Email is required');
    setSaving(true);
    try {
      const res = await apiPost('/portal/admin/accounts', form);
      onCreated({ email: res.email, temp_password: res.temp_password });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to create account'); }
    finally { setSaving(false); }
  };

  return (
    <ModalShell title="New Portal Account" onClose={onClose}>
      <div className="space-y-3">
        <Field label="Company">
          <select value={form.company_id} onChange={set('company_id')} className="input" autoFocus data-testid="portal-account-company">
            <option value="">Select…</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
        <Field label="Contact Name"><input value={form.contact_name} onChange={set('contact_name')} className="input" data-testid="portal-account-contact" /></Field>
        <Field label="Email (their login)"><input type="email" value={form.email} onChange={set('email')} className="input" data-testid="portal-account-email" /></Field>
        <Field label="Phone"><input value={form.phone} onChange={set('phone')} className="input" /></Field>
        <button onClick={save} disabled={saving} className="btn-primary w-full justify-center mt-2" data-testid="portal-account-save">
          {saving ? <Loader2 size={14} className="animate-spin" /> : 'Create Account'}
        </button>
      </div>
    </ModalShell>
  );
}

function CredentialModal({ cred, onClose }) {
  const copy = () => {
    navigator.clipboard?.writeText(`Email: ${cred.email}\nTemporary password: ${cred.temp_password}`);
    toast.success('Copied — share with the customer directly (call/WhatsApp), not email.');
  };
  return (
    <ModalShell title="Share these credentials" onClose={onClose}>
      <div className="space-y-3">
        <p className="text-xs text-slate-500">This password is shown once. Relay it to the customer directly — never paste it into email or chat logs.</p>
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-sm space-y-1">
          <div><span className="text-slate-400 text-xs">Email</span><div className="font-mono">{cred.email}</div></div>
          <div><span className="text-slate-400 text-xs">Temporary Password</span><div className="font-mono font-bold text-base">{cred.temp_password}</div></div>
        </div>
        <button onClick={copy} className="btn-secondary w-full justify-center flex items-center gap-2"><Copy size={13} /> Copy to clipboard</button>
        <button onClick={onClose} className="btn-primary w-full justify-center">Done</button>
      </div>
    </ModalShell>
  );
}
